// src/EZLookup.js
import { useMemo, useState, useEffect } from "react";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { apiFetch } from "./api";

export default function EZLookup({ site }) {
  const [step, setStep] = useState("departments"); // departments | equipment | parts
  const [departments, setDepts] = useState([]);
  const [equipment, setEquip] = useState([]);
  const [selection, setSel] = useState({ dept: null, equip: null });
  const [parts, setParts] = useState([]);
  const [err, setErr] = useState("");

  // ✅ Filters (desktop-equivalent)
  const [fPart, setFPart] = useState("");
  const [fDesc, setFDesc] = useState("");
  const [fLoc, setFLoc] = useState("All");
  const [fShelve, setFShelve] = useState("");
  const [fRow, setFRow] = useState("");
  const [fMfg, setFMfg] = useState("");
  const [fMfgPn, setFMfgPn] = useState("");
  const [fCat, setFCat] = useState("");

  // Location options (site-specific)
  const [locOptions, setLocOptions] = useState(["All"]);

  // reset filters whenever we enter parts step (new equipment selection)
  const resetFilters = () => {
    setFPart("");
    setFDesc("");
    setFLoc("All");
    setFShelve("");
    setFRow("");
    setFMfg("");
    setFMfgPn("");
    setFCat("");
  };

  // load departments on site change
  useEffect(() => {
    if (!site) return;

    let alive = true;
    setErr("");
    setStep("departments");
    setSel({ dept: null, equip: null });
    setEquip([]);
    setParts([]);
    resetFilters();

    apiFetch(`/api/departments?site=${encodeURIComponent(site)}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Departments failed (${res.status})`);
        return res.json();
      })
      .then((data) => {
        if (!alive) return;
        setDepts(data || []);
      })
      .catch((e) => {
        console.error(e);
        if (alive) setErr("Failed to load departments");
      });

    return () => {
      alive = false;
    };
  }, [site]);

  // load site locations (for Location dropdown)
  useEffect(() => {
    if (!site) return;
    let alive = true;

    (async () => {
      try {
        const r = await apiFetch(`/api/parts/locations?site=${encodeURIComponent(site)}`);
        const data = r.ok ? await r.json() : [];
        if (!alive) return;
        const list = Array.isArray(data) ? data : [];
        setLocOptions(["All", ...list]);
      } catch {
        if (!alive) return;
        setLocOptions(["All"]);
      }
    })();

    return () => {
      alive = false;
    };
  }, [site]);

  const chooseDept = (deptName) => {
    setErr("");
    setSel({ dept: deptName, equip: null });
    setEquip([]);
    setParts([]);
    resetFilters();

    apiFetch(`/api/equipment?site=${encodeURIComponent(site)}&department=${encodeURIComponent(deptName)}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Equipment failed (${res.status})`);
        return res.json();
      })
      .then((list) => {
        setEquip(list || []);
        setStep("equipment");
      })
      .catch((e) => {
        console.error(e);
        setErr("Failed to load equipment");
      });
  };

  const chooseEquip = (equipName) => {
    setErr("");
    setSel((sel) => ({ ...sel, equip: equipName }));
    setParts([]);
    resetFilters();

    apiFetch(`/api/parts/by-equipment?site=${encodeURIComponent(site)}&equipment=${encodeURIComponent(equipName)}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Parts failed (${res.status})`);
        return res.json();
      })
      .then((list) => {
        setParts(list || []);
        setStep("parts");
      })
      .catch((e) => {
        console.error(e);
        setErr("Failed to load parts for equipment");
      });
  };

  // ✅ Client-side filtering (desktop-like)
  const filteredParts = useMemo(() => {
    const needlePart = (fPart || "").trim().toLowerCase();
    const needleDesc = (fDesc || "").trim().toLowerCase();
    const needleShelve = (fShelve || "").trim().toLowerCase();
    const needleRow = (fRow || "").trim().toLowerCase();
    const needleMfg = (fMfg || "").trim().toLowerCase();
    const needleMfgPn = (fMfgPn || "").trim().toLowerCase();
    const needleCat = (fCat || "").trim().toLowerCase();

    const locFilter = (fLoc || "All").trim();

    return (parts || []).filter((p) => {
      const pn = String(p["Part Number"] || "").trim().toLowerCase();
      const desc = String(p.Description || "").trim().toLowerCase();
      const loc = String(p.Location || "").trim();
      const shelve = String(p.Shelve || "").trim().toLowerCase();
      const row = String(p.Row || "").trim().toLowerCase();
      const mfg = String(p.Manufacturer || "").trim().toLowerCase();
      const mfgPn = String(p["Manufacturer Part#"] || p["Manufacturer Part Number"] || "").trim().toLowerCase();
      const cat = String(p.Category || "").trim().toLowerCase();

      // Location exact match (desktop behavior)
      if (locFilter && locFilter !== "All") {
        if (locFilter !== loc) return false;
      }

      if (needlePart && !pn.includes(needlePart)) return false;
      if (needleDesc && !desc.includes(needleDesc)) return false;
      if (needleShelve && !shelve.includes(needleShelve)) return false;
      if (needleRow && !row.includes(needleRow)) return false;
      if (needleMfg && !mfg.includes(needleMfg)) return false;
      if (needleMfgPn && !mfgPn.includes(needleMfgPn)) return false;
      if (needleCat && !cat.includes(needleCat)) return false;

      return true;
    });
  }, [parts, fPart, fDesc, fLoc, fShelve, fRow, fMfg, fMfgPn, fCat]);

  // Departments
  if (step === "departments") {
    return (
      <div className="p-5">
        <h2 className="text-2xl font-semibold mb-4">EZ Lookup • Departments</h2>
        {err && <p className="mb-3 text-red-600">{err}</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {departments.map((d) => (
            <button
              key={d.name}
              onClick={() => chooseDept(d.name)}
              className="bg-blue-500 hover:bg-blue-600 text-white rounded-lg py-4 px-2 font-medium transition"
              type="button"
            >
              {d.name}
            </button>
          ))}
          {departments.length === 0 && !err && <p className="text-gray-600">No departments found.</p>}
        </div>
      </div>
    );
  }

  // Equipment
  if (step === "equipment") {
    return (
      <div className="p-5">
        <h2 className="text-xl font-semibold mb-2">Dept: {selection.dept}</h2>
        {err && <p className="mb-3 text-red-600">{err}</p>}

        <button
          onClick={() => setStep("departments")}
          className="mb-4 inline-flex items-center space-x-2 px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-gray-800 transition"
          type="button"
        >
          <ChevronLeftIcon className="h-5 w-5" />
          <span>Back</span>
        </button>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {equipment.map((e) => (
            <button
              key={e}
              onClick={() => chooseEquip(e)}
              className="bg-green-500 hover:bg-green-600 text-white rounded-lg py-4 font-medium transition"
              type="button"
            >
              {e}
            </button>
          ))}
          {equipment.length === 0 && !err && <p className="text-gray-600">No equipment found.</p>}
        </div>
      </div>
    );
  }

  // Parts + Filters
  return (
    <div className="p-5">
      <h2 className="text-xl font-semibold mb-2">
        {selection.dept} → {selection.equip}
      </h2>

      {err && <p className="mb-3 text-red-600">{err}</p>}

      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setStep("equipment")}
          className="inline-flex items-center space-x-2 px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-gray-800 transition"
          type="button"
        >
          <ChevronLeftIcon className="h-5 w-5" />
          <span>Back</span>
        </button>

        <button
          onClick={resetFilters}
          className="px-3 py-1 bg-white border rounded hover:bg-gray-50 text-gray-800 transition"
          type="button"
        >
          Reset Filters
        </button>

        <div className="ml-auto text-sm text-gray-600 flex items-center">
          Showing <b className="mx-1">{filteredParts.length}</b> of <b className="mx-1">{parts.length}</b>
        </div>
      </div>

      {/* ✅ Filter bar */}
      <div className="border rounded-lg p-4 bg-white shadow-sm mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-600">Part Number</label>
            <input className="border rounded px-2 py-2 w-full" value={fPart} onChange={(e) => setFPart(e.target.value)} />
          </div>

          <div>
            <label className="text-xs text-gray-600">Description</label>
            <input className="border rounded px-2 py-2 w-full" value={fDesc} onChange={(e) => setFDesc(e.target.value)} />
          </div>

          <div>
            <label className="text-xs text-gray-600">Location</label>
            <select className="border rounded px-2 py-2 w-full" value={fLoc} onChange={(e) => setFLoc(e.target.value)}>
              {locOptions.map((x) => (
                <option key={x} value={x}>{x}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600">Shelve</label>
            <input className="border rounded px-2 py-2 w-full" value={fShelve} onChange={(e) => setFShelve(e.target.value)} />
          </div>

          <div>
            <label className="text-xs text-gray-600">Row</label>
            <input className="border rounded px-2 py-2 w-full" value={fRow} onChange={(e) => setFRow(e.target.value)} />
          </div>

          <div>
            <label className="text-xs text-gray-600">Manufacturer</label>
            <input className="border rounded px-2 py-2 w-full" value={fMfg} onChange={(e) => setFMfg(e.target.value)} />
          </div>

          <div>
            <label className="text-xs text-gray-600">Manufacturer Part#</label>
            <input className="border rounded px-2 py-2 w-full" value={fMfgPn} onChange={(e) => setFMfgPn(e.target.value)} />
          </div>

          <div>
            <label className="text-xs text-gray-600">Category</label>
            <input className="border rounded px-2 py-2 w-full" value={fCat} onChange={(e) => setFCat(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Parts list */}
      <ul className="space-y-2">
        {filteredParts.map((p) => {
          const pn = p["Part Number"];
          const qty = p["Total Quantity"];
          const desc = p.Description;

          const loc = p.Location || "";
          const shelve = p.Shelve || "";
          const row = p.Row || "";

          return (
            <li key={pn} className="border rounded-lg p-4 bg-white shadow-sm">
              <div className="flex flex-col gap-1">
                <div>
                  <span className="font-mono font-semibold">{pn}</span>
                  <span className="ml-2 text-gray-700">{desc}</span>
                </div>

                <div className="text-sm text-gray-700">
                  <span className="font-semibold text-indigo-600">Qty:</span> {qty}
                  <span className="ml-4 font-semibold">Loc:</span> {loc || "-"}
                  <span className="ml-4 font-semibold">Shelve:</span> {shelve || "-"}
                  <span className="ml-4 font-semibold">Row:</span> {row || "-"}
                </div>
              </div>
            </li>
          );
        })}

        {filteredParts.length === 0 && !err && (
          <li className="text-gray-600">No parts match the filters.</li>
        )}
      </ul>
    </div>
  );
}
